/**
 * Loader Manager
 * Handles skeleton loading UI while iframe is initializing
 */

import { ELEMENT_IDS } from '../constants/sdk-constants';
import {
  getColorScheme,
  getShimmerGradient,
  getLoaderContainerStyles,
  getSkeletonCardStyles,
  getInputWrapStyles,
  getCardNumberSkeletonStyles,
  getExpirySkeletonStyles,
  getCvcSkeletonStyles,
  getButtonSkeletonStyles,
  getDisclaimerSkeletonStyles,
  getDisclaimerIconSkeletonStyles,
  getDisclaimerTextSkeletonStyles,
  SHIMMER_ANIMATION,
} from './styles';

export interface LoaderConfig {
  fillParent?: boolean;
}

export class LoaderManager {
  private container: HTMLElement;
  private config: LoaderConfig;
  private loaderElement: HTMLElement | null = null;

  constructor(container: HTMLElement, config: LoaderConfig = {}) {
    this.container = container;
    this.config = config;
  }

  /**
   * Show skeleton loader
   */
  show(iframe?: HTMLIFrameElement): void {
    // Hide iframe while loader is showing
    if (iframe) {
      iframe.style.display = 'none';
    }

    // Detect dark mode
    const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

    // Get color scheme
    const colors = getColorScheme(isDarkMode);
    const shimmerGradient = getShimmerGradient(colors);

    // Create loader container
    const loader = document.createElement('div');
    loader.id = ELEMENT_IDS.LOADER;
    loader.style.cssText = getLoaderContainerStyles();

    // Create skeleton card element
    const skeletonCard = document.createElement('div');
    skeletonCard.style.cssText = getSkeletonCardStyles(this.config.fillParent || false);

    // Create input wrapper
    const inputWrap = document.createElement('div');
    inputWrap.style.cssText = getInputWrapStyles(colors.inputBgColor);

    // Create card number skeleton
    const cardNumberSkeleton = document.createElement('div');
    cardNumberSkeleton.className = 'inflowpay-skeleton';
    cardNumberSkeleton.style.cssText = getCardNumberSkeletonStyles(shimmerGradient);

    // Create expiry skeleton
    const expirySkeleton = document.createElement('div');
    expirySkeleton.className = 'inflowpay-skeleton';
    expirySkeleton.style.cssText = getExpirySkeletonStyles(shimmerGradient);

    // Create CVC skeleton
    const cvcSkeleton = document.createElement('div');
    cvcSkeleton.className = 'inflowpay-skeleton';
    cvcSkeleton.style.cssText = getCvcSkeletonStyles(shimmerGradient);

    // Assemble input wrapper
    inputWrap.appendChild(cardNumberSkeleton);
    inputWrap.appendChild(expirySkeleton);
    inputWrap.appendChild(cvcSkeleton);

    // Create button skeleton
    const buttonSkeleton = document.createElement('div');
    buttonSkeleton.className = 'inflowpay-skeleton';
    buttonSkeleton.style.cssText = getButtonSkeletonStyles(shimmerGradient);

    // Create disclaimer skeleton
    const disclaimerSkeleton = document.createElement('div');
    disclaimerSkeleton.style.cssText = getDisclaimerSkeletonStyles();

    const disclaimerIconSkeleton = document.createElement('div');
    disclaimerIconSkeleton.className = 'inflowpay-skeleton';
    disclaimerIconSkeleton.style.cssText = getDisclaimerIconSkeletonStyles(shimmerGradient);

    const disclaimerTextSkeleton = document.createElement('div');
    disclaimerTextSkeleton.className = 'inflowpay-skeleton';
    disclaimerTextSkeleton.style.cssText = getDisclaimerTextSkeletonStyles(shimmerGradient);

    disclaimerSkeleton.appendChild(disclaimerIconSkeleton);
    disclaimerSkeleton.appendChild(disclaimerTextSkeleton);

    // Assemble skeleton card
    skeletonCard.appendChild(inputWrap);
    skeletonCard.appendChild(buttonSkeleton);
    skeletonCard.appendChild(disclaimerSkeleton);

    // Add to loader
    loader.appendChild(skeletonCard);

    // Inject shimmer animation styles if not already present
    this.injectShimmerStyles();

    // Add loader to container
    this.container.appendChild(loader);
    this.loaderElement = loader;
  }

  /**
   * Hide loader
   */
  hide(iframe?: HTMLIFrameElement): void {
    const loader = document.getElementById(ELEMENT_IDS.LOADER);
    if (loader) {
      loader.remove();
    }
    this.loaderElement = null;

    // Show iframe again when loader is hidden
    if (iframe) {
      iframe.style.display = '';
    }
  }

  /**
   * Inject shimmer animation styles into document head
   */
  private injectShimmerStyles(): void {
    if (!document.getElementById(ELEMENT_IDS.LOADER_STYLES)) {
      const style = document.createElement('style');
      style.id = ELEMENT_IDS.LOADER_STYLES;
      style.textContent = SHIMMER_ANIMATION;
      document.head.appendChild(style);
    }
  }
}
